import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * 안정화된 새 창 포털 (최소 수정)
 * - 같은 name 재사용으로 StrictMode 깜빡임 방지
 * - 자식창 닫힘 감지는 beforeunload 사용 (타이머 제거)
 * - cleanup에서 창을 닫지 않음(개발모드 이중 마운트 대비)
 */
export default function NewWindow({
  children,
  title = "Detail",
  features = "width=900,height=700,left=120,top=60",
  onClose,
  copyStyles = true,
  closeOnUnmount = true,
}) {
  const winRef = useRef(null);
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  // 부모 쪽 포털 컨테이너 (1회 생성)
  if (!containerRef.current) {
    containerRef.current = document.createElement("div");
    containerRef.current.id = "scms-portal";
  }

  useEffect(() => {
    const NAME = "SCMS_POPUP"; // ✅ 같은 이름으로 재사용

    // 1) 창 열기(이미 열려 있으면 재사용)
    const win = window.open("", NAME, features);
    if (!win) {
      alert("팝업 차단으로 인해 새 창을 열 수 없습니다.");
      return;
    }
    winRef.current = win;

    const doc = win.document;

    // 2) 자식 문서 뼈대/타이틀 보정 (이미 만들어져 있으면 재사용)
    if (!doc.body || !doc.getElementById("scms-portal")) {
      // (최소 수정) 기존 로직과 유사하게 뼈대 작성
      doc.open();
      doc.write(
        `<!doctype html><html><head><title>${title}</title><meta charset="utf-8"/></head><body style="margin:0"></body></html>`
      );
      doc.close();

      // 3) 스타일 복사 (원 코드 유지)
      if (copyStyles) {
        const head = doc.head;
        Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).forEach((node) => {
          head.appendChild(node.cloneNode(true));
        });
      }

      // 4) 포털 컨테이너 부착
      doc.body.appendChild(containerRef.current);
    } else {
      // 이미 열려 있고 포털 div도 존재 → 타이틀만 동기화
      if (title) doc.title = title;
      // 기존 포털이 자식창에 있지만, 부모의 containerRef를 가리키도록 보정
      // (부모 재마운트 시 ref가 새로 생겼기 때문)
      const existing = doc.getElementById("scms-portal");
      if (existing && existing !== containerRef.current) {
        existing.replaceWith(containerRef.current);
      }
    }

    // 5) 렌더 시작
    setReady(true);

    // 6) 자식창 닫힘 감지 (타이머 대신 beforeunload)
    const handleChildClose = () => {
      onClose && onClose();
    };
    win.addEventListener("beforeunload", handleChildClose, { once: true });

    // 7) 부모창 닫힘 시(새로고침 등) 자식창 정리: 개발중 깜빡임 방지를 위해 닫지 않음
    const handleParentUnload = () => {
      // 필요하면 여기서 win.close() 호출
    };
    window.addEventListener("beforeunload", handleParentUnload);

    // ✅ cleanup: 리스너만 제거. 자식창은 닫지 않음(StrictMode 이중 마운트 깜빡임 방지)
    return () => {
      try {
        win.removeEventListener("beforeunload", handleChildClose);
      } catch {}
      window.removeEventListener("beforeunload", handleParentUnload);
      // try { win.close(); }catch{}  ← 닫지 않음!
      
      // about:blank 방지용: 언마운트 시 실제로 닫기
      if (closeOnUnmount && win && !win.closed) {
        try { win.close(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, features, copyStyles, onClose, closeOnUnmount]); // (최소 수정) 의존성 그대로

  if (!ready || !winRef.current) return null;
  
  // ✅ children이 함수면 포털 대상(container)을 넘겨 렌더
  const content = typeof children === "function"
    ? children(containerRef.current)
    : children;

  return createPortal(content, containerRef.current);

}
